import throttle from 'lodash/throttle';
import { create, type StateCreator } from 'zustand';

import { OrderbookViewMode } from './types';

/**
 * WebSocket connection status for orderbook
 * Maps from WebSocketStatus
 */
export type OrderbookConnectionStatus =
  | 'connecting'
  | 'connected'
  | 'reconnecting'
  | 'disconnected'
  | 'error';

export interface RawOrderbookEntry {
  price: string;
  amount: string;
  hasOrders?: boolean;
}

interface OrderbookPairData {
  asks: RawOrderbookEntry[];
  bids: RawOrderbookEntry[];
  lastPrice: string;
  timestamp: number;
}

interface OrderbookPairSettings {
  tickSize: number | null;
  baseTickSize: number | null; // Initial minimum tick size from exchange (never changes)
  viewMode: OrderbookViewMode;
}

interface OrderbookState {
  orderbooks: Record<number, OrderbookPairData>;
  settings: Record<number, OrderbookPairSettings>;
  loading: Record<number, boolean>;
  errors: Record<number, Error | null>;
  subscriptions: Record<number, number>;
  connectionStatus: Record<number, OrderbookConnectionStatus>;
  lastUpdateTimestamp: Record<number, number>;
}

interface OrderbookActions {
  updateOrderbook: (
    pairId: number,
    data: { asks: RawOrderbookEntry[]; bids: RawOrderbookEntry[] },
  ) => void;
  updateOrderbookThrottled: (
    pairId: number,
    data: { asks: RawOrderbookEntry[]; bids: RawOrderbookEntry[] },
  ) => void;
  updateLastPrice: (pairId: number, price: string) => void;
  setLoading: (pairId: number, loading: boolean) => void;
  setError: (pairId: number, error: Error | null) => void;
  setTickSize: (pairId: number, tickSize: number | null) => void;
  setViewMode: (pairId: number, mode: OrderbookViewMode) => void;
  initializeSettings: (pairId: number, settings: Partial<OrderbookPairSettings>) => void;
  subscribe: (pairId: number) => void;
  unsubscribe: (pairId: number) => void;
  getSubscriptionCount: (pairId: number) => number;
  clearPair: (pairId: number) => void;
  reset: () => void;
  setConnectionStatus: (pairId: number, status: OrderbookConnectionStatus) => void;
}

export type OrderbookStoreState = OrderbookState & OrderbookActions;

// Maximum number of orderbook entries per side to prevent memory bloat
// Most exchanges provide 100-1000 depth levels, we keep top 500
const MAX_ORDERBOOK_DEPTH = 125;

const DEFAULT_SETTINGS: OrderbookPairSettings = {
  tickSize: 0.01,
  baseTickSize: null,
  viewMode: 'all',
};

const orderbookStoreSlice: StateCreator<OrderbookStoreState> = (set, get) => {
  // Create throttled update function (once!)
  // This reduces React re-renders from 100+/sec to 10/sec during high volatility
  const throttledUpdate = throttle(
    (pairId: number, data: { asks: RawOrderbookEntry[]; bids: RawOrderbookEntry[] }) => {
      get().updateOrderbook(pairId, data);
    },
    100, // 100ms throttle = 10fps max
    {
      leading: false, // Don't update on first call
      trailing: true, // Update after throttle window
    },
  );

  return {
    orderbooks: {},
    settings: {},
    loading: {},
    errors: {},
    subscriptions: {},
    connectionStatus: {},
    lastUpdateTimestamp: {},

    updateOrderbook: (pairId, data) =>
      set(state => {
        const existing = state.orderbooks[pairId] || {
          asks: [],
          bids: [],
          lastPrice: '0',
          timestamp: Date.now(),
        };

        // Limit asks and bids to prevent memory bloat
        // Keep only top MAX_ORDERBOOK_DEPTH entries
        const limitedAsks =
          data.asks.length > 0 ? data.asks.slice(0, MAX_ORDERBOOK_DEPTH) : existing.asks;
        const limitedBids =
          data.bids.length > 0 ? data.bids.slice(0, MAX_ORDERBOOK_DEPTH) : existing.bids;

        return {
          orderbooks: {
            ...state.orderbooks,
            [pairId]: {
              asks: limitedAsks,
              bids: limitedBids,
              lastPrice: existing.lastPrice,
              timestamp: Date.now(),
            },
          },
          lastUpdateTimestamp: {
            ...state.lastUpdateTimestamp,
            [pairId]: Date.now(),
          },
        };
      }),

    updateOrderbookThrottled: (pairId, data) => {
      throttledUpdate(pairId, data);
    },

    updateLastPrice: (pairId, price) =>
      set(state => ({
        orderbooks: {
          ...state.orderbooks,
          [pairId]: {
            ...(state.orderbooks[pairId] || {
              asks: [],
              bids: [],
              timestamp: Date.now(),
            }),
            lastPrice: price,
          },
        },
      })),

    setLoading: (pairId, loading) =>
      set(state => ({
        loading: {
          ...state.loading,
          [pairId]: loading,
        },
      })),

    setError: (pairId, error) =>
      set(state => ({
        errors: {
          ...state.errors,
          [pairId]: error,
        },
      })),

    setTickSize: (pairId, tickSize) =>
      set(state => ({
        settings: {
          ...state.settings,
          [pairId]: {
            ...(state.settings[pairId] || DEFAULT_SETTINGS),
            tickSize,
          },
        },
      })),

    setViewMode: (pairId, mode) =>
      set(state => ({
        settings: {
          ...state.settings,
          [pairId]: {
            ...(state.settings[pairId] || DEFAULT_SETTINGS),
            viewMode: mode,
          },
        },
      })),

    initializeSettings: (pairId, settings) =>
      set(state => ({
        settings: {
          ...state.settings,
          [pairId]: {
            ...DEFAULT_SETTINGS,
            ...state.settings[pairId],
            ...settings,
          },
        },
      })),

    subscribe: pairId =>
      set(state => {
        const currentCount = state.subscriptions[pairId] || 0;
        return {
          subscriptions: {
            ...state.subscriptions,
            [pairId]: currentCount + 1,
          },
          loading: {
            ...state.loading,
            [pairId]: true,
          },
          // Initialize lastUpdateTimestamp to 0 on first subscription
          lastUpdateTimestamp:
            currentCount === 0
              ? {
                  ...state.lastUpdateTimestamp,
                  [pairId]: 0,
                }
              : state.lastUpdateTimestamp,
          // Initialize connection status to 'connecting' on first subscription
          connectionStatus:
            currentCount === 0
              ? {
                  ...state.connectionStatus,
                  [pairId]: 'connecting',
                }
              : state.connectionStatus,
        };
      }),

    unsubscribe: pairId =>
      set(state => {
        const currentCount = state.subscriptions[pairId] || 0;
        const nextCount = Math.max(0, currentCount - 1);
        return {
          subscriptions: {
            ...state.subscriptions,
            [pairId]: nextCount,
          },
        };
      }),

    getSubscriptionCount: pairId => get().subscriptions[pairId] || 0,

    setConnectionStatus: (pairId, status) =>
      set(state => ({
        connectionStatus: {
          ...state.connectionStatus,
          [pairId]: status,
        },
      })),

    clearPair: pairId =>
      set(state => {
        const nextOrderbooks = { ...state.orderbooks };
        const nextSettings = { ...state.settings };
        delete nextOrderbooks[pairId];
        delete nextSettings[pairId];

        const nextLoading = { ...state.loading };
        const nextErrors = { ...state.errors };
        const nextSubscriptions = { ...state.subscriptions };
        const nextConnectionStatus = { ...state.connectionStatus };
        const nextLastUpdateTimestamp = { ...state.lastUpdateTimestamp };
        delete nextLoading[pairId];
        delete nextErrors[pairId];
        delete nextSubscriptions[pairId];
        delete nextConnectionStatus[pairId];
        delete nextLastUpdateTimestamp[pairId];

        return {
          orderbooks: nextOrderbooks,
          settings: nextSettings,
          loading: nextLoading,
          errors: nextErrors,
          subscriptions: nextSubscriptions,
          connectionStatus: nextConnectionStatus,
          lastUpdateTimestamp: nextLastUpdateTimestamp,
        };
      }),

    reset: () =>
      set({
        orderbooks: {},
        settings: {},
        loading: {},
        errors: {},
        subscriptions: {},
        connectionStatus: {},
        lastUpdateTimestamp: {},
      }),
  };
};

export const useOrderbookStore = create<OrderbookStoreState>(orderbookStoreSlice);
